import React from 'react'
import styled from 'styled-components'
import { CardRow } from '../../../../../../generic/ui/CardRow'
import { Mono } from '../../../../../../generic/ui/Mono'
import { Tag } from '../../../../../../generic/ui/Tag'
import BarcodeIcon from '../../../../../../generic/icons/IconLib/BarcodeIcon'
import UserIcon from '../../../../../../generic/icons/IconLib/UserIcon'
import SproutIcon from '../../../../../../generic/icons/IconLib/SproutIcon'
import WarehouseIcon from '../../../../../../generic/icons/IconLib/WarehouseIcon'
import LinkIcon from '../../../../../../generic/icons/IconLib/LinkIcon'
import { ISortingListItem } from '../../../../../../modules/API/Miun/models/sortingList'
import { convertToDate } from '../../../../../../modules/convertDateString'

interface Props {
    data: ISortingListItem
    givenCount?: number
}

const WAREHOUSE_COLOR = '#0891B2'

const MiunTitleCard: React.FC<Props> = ({ data, givenCount = 0 }) => {
    const date = data.Date_Sorting ? convertToDate(data.Date_Sorting) : ''
    const grower = (data.GrowerName ?? '').trim()
    const warehouse = (data.WarehouseDesc ?? '').trim()
    const product = `${(data.ProductDesc ?? '').trim()} ${(data.VarietyDesc ?? '').trim()}`.trim()
    const reference = data.Sorting_Reference ? String(data.Sorting_Reference) : ''

    return (
        <Card>
            <CardRow
                $columns='5rem minmax(0, 1fr) auto auto'
                $mobileColumns='auto minmax(0, 1fr) auto auto'
                $isLast
                $hover={false}
            >
                <CardRow.Cell>
                    <CardRow.Stack $gap='0.125rem'>
                        <CardRow.Line $gap='0.375rem'>
                            <SmallIcon>
                                <BarcodeIcon color='currentColor' size={16} />
                            </SmallIcon>
                            <Id>{data.Sorting_Num}</Id>
                        </CardRow.Line>
                        {reference && (
                            <CardRow.Line $gap='0.25rem'>
                                <SmallIcon $muted>
                                    <LinkIcon color='currentColor' size={12} />
                                </SmallIcon>
                                <Reference>{reference}</Reference>
                            </CardRow.Line>
                        )}
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell>
                    <CardRow.Stack $gap='0.375rem'>
                        {product && (
                            <CardRow.Line $gap='0.5rem'>
                                <SmallIcon $muted>
                                    <SproutIcon color='currentColor' size={16} />
                                </SmallIcon>
                                <PrimaryName>{product}</PrimaryName>
                            </CardRow.Line>
                        )}
                        {grower && (
                            <CardRow.Line $gap='0.5rem'>
                                <SmallIcon $muted>
                                    <UserIcon color='currentColor' size={14} />
                                </SmallIcon>
                                <SecondaryName>{grower}</SecondaryName>
                            </CardRow.Line>
                        )}
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell $align='center'>
                    <CardRow.Stack $gap='0.25rem' $align='center'>
                        {date && <When>{date}</When>}
                        {warehouse && (
                            <Tag variant='tinted' color={WAREHOUSE_COLOR}>
                                <ChipInner>
                                    <ChipIcon>
                                        <WarehouseIcon />
                                    </ChipIcon>
                                    <ChipText>{warehouse}</ChipText>
                                </ChipInner>
                            </Tag>
                        )}
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell $align='center'>
                    <CounterStack>
                        <CounterValue>{givenCount}</CounterValue>
                        <CounterLabel>במיון</CounterLabel>
                    </CounterStack>
                </CardRow.Cell>
            </CardRow>
        </Card>
    )
}

export default MiunTitleCard

const Card = styled.div`
    position: relative;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1rem;
    background: ${({ theme }) => theme.colors.surface};
    overflow: hidden;
`

const SmallIcon = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $muted }) => ($muted ? theme.colors.ink3 : theme.colors.ink2)};
    flex-shrink: 0;
`

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`
const Reference = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink4};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`
const PrimaryName = styled.span`
    font-size: ${({ theme }) => theme.text.lg};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
    white-space: nowrap;
    font-family: ${({ theme }) => theme.fonts.sans};
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`
const SecondaryName = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;
    font-family: ${({ theme }) => theme.fonts.sans};
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`
const When = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink2};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`
const ChipInner = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: inherit;
`
const ChipIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: inherit;
    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`
const ChipText = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 0.125rem;
`
const CounterStack = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.125rem;
    min-width: 2.75rem;
    padding: 0.25rem 0.375rem;
    border-radius: 0.75rem;
    background: ${({ theme }) => theme.colors.accentSoft};
`
const CounterValue = styled.span`
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    line-height: 1;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`
const CounterLabel = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    line-height: 1;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`
